/**
 * GV100 Download API client
 *
 * Handles communication with the backend for GV100 automatic download
 * from Destatis and import functionality.
 */

import axios from 'axios'
import { attachAuth } from '../../lib/apiAuth'

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8899'

const api = axios.create({
  baseURL: `${API_BASE}/admin`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// ============================================================================
// Types
// ============================================================================

export interface GV100VersionInfo {
  year: number
  edition_name: string
  reference_date: string
  reference_year: number
  download_url: string
  is_installed: boolean
  installed_edition_id: number | null
  installed_date: string | null
}

export interface GV100AvailableVersionsResponse {
  versions: GV100VersionInfo[]
  installed_count: number
  available_count: number
}

export interface GV100DownloadResponse {
  status: string
  job_id: string
  celery_task_id?: string
  year: number
  edition_name: string
  message: string
}

export interface GV100DownloadStatus {
  job_id: string
  status: 'pending' | 'queued' | 'running' | 'completed' | 'failed'
  progress: number
  message: string
  year: number | null
  edition_name: string | null
  ad_path: string | null
  nad_path: string | null
  error: string | null
}

export interface GV100ImportDownloadedParams {
  ad_path: string
  nad_path?: string | null
  edition_mode: 'create_new' | 'update_existing'
  edition_name?: string
  target_edition_id?: number
  background?: boolean
}

export interface GV100ImportResponse {
  status: string
  job_id?: string
  message?: string
  edition_name?: string
  edition_mode?: string
  reference_year?: number
  total_records?: number
  successful_records?: number
  failed_records?: number
  skipped_records?: number
  errors?: string[]
  warnings?: string[]
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Get list of available GV100 versions from Destatis.
 * Includes installed and not-yet-installed versions.
 */
export async function getAvailableVersions(): Promise<GV100AvailableVersionsResponse> {
  const response = await api.get<GV100AvailableVersionsResponse>(
    '/import/gv100/available-versions'
  )
  return response.data
}

/**
 * Start download of a specific GV100 version from Destatis.
 */
export async function startDownload(year: number): Promise<GV100DownloadResponse> {
  const response = await api.post<GV100DownloadResponse>(
    '/import/gv100/download',
    null,
    { params: { year } }
  )
  return response.data
}

/**
 * Get status of a GV100 download job.
 */
export async function getDownloadStatus(jobId: string): Promise<GV100DownloadStatus> {
  const response = await api.get<GV100DownloadStatus>(
    `/import/gv100/download/status/${jobId}`
  )
  return response.data
}

/**
 * Import downloaded GV100 files.
 */
export async function importDownloaded(
  params: GV100ImportDownloadedParams
): Promise<GV100ImportResponse> {
  const response = await api.post<GV100ImportResponse>(
    '/import/gv100/import-downloaded',
    null,
    {
      params: {
        ad_path: params.ad_path,
        nad_path: params.nad_path || undefined,
        edition_mode: params.edition_mode,
        edition_name: params.edition_name || undefined,
        target_edition_id: params.target_edition_id || undefined,
        background: params.background ?? true,
      },
    }
  )
  return response.data
}

// Basic-Auth-Header + 401-Behandlung (optionaler Schutz, v0.3.8)
attachAuth(api)
